import * as XLSX from 'xlsx';
import { getFlocs, createFloc, updateFloc } from './flocService';
import { getAssets, createAsset, updateAsset, CreateAssetData } from './assetService';
import { getTemplates } from './templateService';
import { getSchedules, createSchedule, updateSchedule, CreateScheduleData } from './scheduleService';

/**
 * Remove all undefined values from an object (Firebase doesn't accept undefined)
 */
function removeUndefinedValues(obj: any): any {
  const result: any = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Export all configuration data to Excel file
 */
export async function exportConfiguration(orgId: string): Promise<void> {
  try {
    // Fetch all data
    const [flocs, assets, templates, schedules] = await Promise.all([
      getFlocs(orgId),
      getAssets(orgId),
      getTemplates(orgId),
      getSchedules(orgId),
    ]);

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Add FLOCs sheet
    const flocsData = flocs.map(floc => ({
      ID: floc.id,
      Code: floc.code,
      Name: floc.name,
      Description: floc.description || '',
      'Parent FLOC ID': floc.parentFlocId || '',
      Level: floc.level,
      Type: floc.type,
      'Functional System': floc.functionalSystem || '',
      Criticality: floc.criticality,
      'Operational Status': floc.operationalStatus,
      'Installed Asset ID': floc.installedAssetId || '',
      'Child Count': floc.childCount || 0,
      Address: floc.address || '',
    }));
    const flocsSheet = XLSX.utils.json_to_sheet(flocsData);
    XLSX.utils.book_append_sheet(wb, flocsSheet, 'FLOCs');

    // Add Assets sheet
    const assetsData = assets.map(asset => ({
      ID: asset.id,
      Name: asset.name,
      'Asset Code': asset.assetId,
      'Serial Number': asset.serialNumber,
      Type: asset.type || '',
      Category: asset.category || '',
      Manufacturer: asset.manufacturer || '',
      Model: asset.model || '',
      'Lifecycle Status': asset.lifecycleStatus,
      'Current FLOC ID': asset.currentFlocId || '',
      'Current FLOC': flocs.find(f => f.id === asset.currentFlocId)?.code || '',
      'Storage Location': asset.storageLocation || '',
      'Condition Score': asset.conditionScore,
      'Condition Grade': asset.conditionGrade,
      Description: asset.description || '',
    }));
    const assetsSheet = XLSX.utils.json_to_sheet(assetsData);
    XLSX.utils.book_append_sheet(wb, assetsSheet, 'Assets');

    // Add Templates sheet
    const templatesData = templates.map(template => ({
      ID: template.id,
      Name: template.name,
      Description: template.description || '',
      Version: template.version,
      'Section Count': template.sections?.length || 0,
    }));
    const templatesSheet = XLSX.utils.json_to_sheet(templatesData);
    XLSX.utils.book_append_sheet(wb, templatesSheet, 'Templates');

    // Add Schedules sheet
    const schedulesData = schedules.map(schedule => ({
      ID: schedule.id,
      Name: schedule.name,
      Description: schedule.description || '',
      'Asset ID': schedule.assetId || '',
      'Asset Name': assets.find(a => a.id === schedule.assetId)?.name || '',
      'FLOC ID': schedule.siteId || '', // Legacy field name, but now contains FLOC ID
      'FLOC Code': flocs.find(f => f.id === schedule.siteId)?.code || '',
      'Template ID': schedule.templateId,
      'Template Name': templates.find(t => t.id === schedule.templateId)?.name || '',
      Priority: schedule.priority,
      'Recurrence Frequency': schedule.recurrence.frequency,
      'Recurrence Interval': schedule.recurrence.interval,
      'Lead Time (days)': schedule.leadTime || 0,
      'Assigned To': schedule.assignedTo || '',
      'Is Active': schedule.isActive,
      'Approval Required': schedule.approvalRequired || false,
    }));
    const schedulesSheet = XLSX.utils.json_to_sheet(schedulesData);
    XLSX.utils.book_append_sheet(wb, schedulesSheet, 'Schedules');

    // Add Instructions sheet
    const instructions = [
      ['EasyDone Pro - Configuration Export'],
      [''],
      ['This file contains all your current configuration data.'],
      ['You can modify existing records or add new ones, then import this file back.'],
      [''],
      ['IMPORTANT RULES:'],
      ['1. Do NOT modify the ID column for existing records'],
      ['2. Leave ID empty for new records (will be auto-generated)'],
      ['3. Site ID, Asset ID, Template ID must reference existing records'],
      ['4. Required fields must not be empty'],
      [''],
      ['SHEET DESCRIPTIONS:'],
      ['- Sites: Physical locations where assets are located'],
      ['- Assets: Equipment/machinery to be inspected'],
      ['- Templates: Inspection form templates'],
      ['- Schedules: Recurring inspection schedules'],
      [''],
      ['For bulk updates: Keep existing IDs and modify other fields'],
      ['For bulk creates: Leave ID empty and fill in all required fields'],
    ];
    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
    XLSX.utils.book_append_sheet(wb, instructionsSheet, 'Instructions');

    // Download file
    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `EasyDone-Configuration-${timestamp}.xlsx`);
  } catch (error) {
    console.error('Failed to export configuration:', error);
    throw new Error('Failed to export configuration');
  }
}

/**
 * Export FLOCs only
 */
export async function exportFlocs(orgId: string): Promise<void> {
  try {
    const flocs = await getFlocs(orgId);

    const flocsData = flocs.map(floc => ({
      ID: floc.id,
      Code: floc.code,
      Name: floc.name,
      Description: floc.description || '',
      'Parent FLOC ID': floc.parentFlocId || '',
      Level: floc.level,
      Type: floc.type,
      'Functional System': floc.functionalSystem || '',
      Criticality: floc.criticality,
      'Operational Status': floc.operationalStatus,
      'Installed Asset ID': floc.installedAssetId || '',
      'Child Count': floc.childCount || 0,
      Address: floc.address || '',
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(flocsData);
    XLSX.utils.book_append_sheet(wb, ws, 'FLOCs');

    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `FLOCs-${timestamp}.xlsx`);
  } catch (error) {
    console.error('Failed to export FLOCs:', error);
    throw new Error('Failed to export FLOCs');
  }
}

/**
 * Export assets only
 */
export async function exportAssets(orgId: string): Promise<void> {
  try {
    const [assets, flocs] = await Promise.all([
      getAssets(orgId),
      getFlocs(orgId),
    ]);

    const assetsData = assets.map(asset => ({
      ID: asset.id,
      Name: asset.name,
      'Asset Code': asset.assetId,
      'Serial Number': asset.serialNumber,
      Type: asset.type || '',
      Category: asset.category || '',
      Manufacturer: asset.manufacturer || '',
      Model: asset.model || '',
      'Lifecycle Status': asset.lifecycleStatus,
      'Current FLOC ID': asset.currentFlocId || '',
      'Current FLOC': flocs.find(f => f.id === asset.currentFlocId)?.code || '',
      'Storage Location': asset.storageLocation || '',
      'Condition Score': asset.conditionScore,
      'Condition Grade': asset.conditionGrade,
      Description: asset.description || '',
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(assetsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Assets');

    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Assets-${timestamp}.xlsx`);
  } catch (error) {
    console.error('Failed to export assets:', error);
    throw new Error('Failed to export assets');
  }
}

/**
 * Export schedules only
 */
export async function exportSchedules(orgId: string): Promise<void> {
  try {
    const [schedules, flocs, assets, templates] = await Promise.all([
      getSchedules(orgId),
      getFlocs(orgId),
      getAssets(orgId),
      getTemplates(orgId),
    ]);

    const schedulesData = schedules.map(schedule => ({
      ID: schedule.id,
      Name: schedule.name,
      Description: schedule.description || '',
      'Asset ID': schedule.assetId || '',
      'Asset Name': assets.find(a => a.id === schedule.assetId)?.name || '',
      'FLOC ID': schedule.siteId || '',
      'FLOC Code': flocs.find(f => f.id === schedule.siteId)?.code || '',
      'Template ID': schedule.templateId,
      'Template Name': templates.find(t => t.id === schedule.templateId)?.name || '',
      Priority: schedule.priority,
      'Recurrence Frequency': schedule.recurrence.frequency,
      'Recurrence Interval': schedule.recurrence.interval,
      'Days of Week': schedule.recurrence.daysOfWeek?.join(',') || '',
      'Day of Month': schedule.recurrence.dayOfMonth || '',
      'Lead Time (days)': schedule.leadTime || 0,
      'Assigned To': schedule.assignedTo || '',
      'Is Active': schedule.isActive,
      'Approval Required': schedule.approvalRequired || false,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(schedulesData);
    XLSX.utils.book_append_sheet(wb, ws, 'Schedules');

    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Schedules-${timestamp}.xlsx`);
  } catch (error) {
    console.error('Failed to export schedules:', error);
    throw new Error('Failed to export schedules');
  }
}

/**
 * Parse and validate imported Excel file
 */
export interface ImportResult {
  success: boolean;
  errors: string[];
  data: {
    flocs?: any[];
    assets?: any[];
    templates?: any[];
    schedules?: any[];
  };
}

export async function parseImportFile(file: File): Promise<ImportResult> {
  try {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data);

    const result: ImportResult = {
      success: true,
      errors: [],
      data: {},
    };

    // Parse FLOCs sheet
    if (wb.SheetNames.includes('FLOCs')) {
      const flocsSheet = wb.Sheets['FLOCs'];
      const flocsData = XLSX.utils.sheet_to_json(flocsSheet);
      result.data.flocs = flocsData;
    }

    // Parse Assets sheet
    if (wb.SheetNames.includes('Assets')) {
      const assetsSheet = wb.Sheets['Assets'];
      const assetsData = XLSX.utils.sheet_to_json(assetsSheet);
      result.data.assets = assetsData;
    }

    // Parse Templates sheet
    if (wb.SheetNames.includes('Templates')) {
      const templatesSheet = wb.Sheets['Templates'];
      const templatesData = XLSX.utils.sheet_to_json(templatesSheet);
      result.data.templates = templatesData;
    }

    // Parse Schedules sheet
    if (wb.SheetNames.includes('Schedules')) {
      const schedulesSheet = wb.Sheets['Schedules'];
      const schedulesData = XLSX.utils.sheet_to_json(schedulesSheet);
      result.data.schedules = schedulesData;
    }

    // Validate data
    if (!result.data.flocs && !result.data.assets && !result.data.templates && !result.data.schedules) {
      result.success = false;
      result.errors.push('No valid sheets found in the file');
    }

    return result;
  } catch (error) {
    console.error('Failed to parse import file:', error);
    return {
      success: false,
      errors: ['Failed to parse Excel file. Please ensure it is a valid .xlsx file.'],
      data: {},
    };
  }
}

/**
 * Execute import of configuration data
 */
export interface ImportExecutionResult {
  success: boolean;
  errors: string[];
  created: {
    flocs: number;
    assets: number;
    schedules: number;
  };
  updated: {
    flocs: number;
    assets: number;
    schedules: number;
  };
}

export async function executeImport(
  orgId: string,
  data: ImportResult['data'],
  userId: string
): Promise<ImportExecutionResult> {
  const result: ImportExecutionResult = {
    success: true,
    errors: [],
    created: { flocs: 0, assets: 0, schedules: 0 },
    updated: { flocs: 0, assets: 0, schedules: 0 },
  };

  try {
    // Import FLOCs first (assets depend on FLOCs)
    if (data.flocs && data.flocs.length > 0) {
      const flocResult = await importFlocs(orgId, data.flocs, userId);
      result.created.flocs = flocResult.created;
      result.updated.flocs = flocResult.updated;
      result.errors.push(...flocResult.errors);
    }

    // Import assets
    if (data.assets && data.assets.length > 0) {
      const assetResult = await importAssets(orgId, data.assets, userId);
      result.created.assets = assetResult.created;
      result.updated.assets = assetResult.updated;
      result.errors.push(...assetResult.errors);
    }

    // Import schedules (depend on FLOCs, assets, and templates)
    if (data.schedules && data.schedules.length > 0) {
      const scheduleResult = await importSchedules(orgId, data.schedules);
      result.created.schedules = scheduleResult.created;
      result.updated.schedules = scheduleResult.updated;
      result.errors.push(...scheduleResult.errors);
    }

    // If there are any errors, mark as not successful
    if (result.errors.length > 0) {
      result.success = false;
    }

    return result;
  } catch (error) {
    console.error('Import execution failed:', error);
    return {
      success: false,
      errors: ['Import execution failed: ' + (error instanceof Error ? error.message : 'Unknown error')],
      created: { flocs: 0, assets: 0, schedules: 0 },
      updated: { flocs: 0, assets: 0, schedules: 0 },
    };
  }
}

/**
 * Import FLOCs from parsed data
 */
async function importFlocs(
  orgId: string,
  flocsData: any[],
  userId: string
): Promise<{ created: number; updated: number; errors: string[] }> {
  let created = 0;
  let updated = 0;
  const errors: string[] = [];

  // Get existing FLOCs to check for updates
  const existingFlocs = await getFlocs(orgId);
  const existingFlocMap = new Map(existingFlocs.map(f => [f.id, f]));

  for (let i = 0; i < flocsData.length; i++) {
    const row = flocsData[i];
    const rowNum = i + 2; // Excel row number (1-indexed + header row)

    try {
      // Validate required fields
      if (!row.Name || typeof row.Name !== 'string') {
        errors.push(`Row ${rowNum}: Name is required`);
        continue;
      }
      if (!row.Code || typeof row.Code !== 'string') {
        errors.push(`Row ${rowNum}: Code is required`);
        continue;
      }

      // Auto-generate type if not provided
      const flocType = row.Type || 'equipment';
      const criticality = row.Criticality || 'medium';

      // Check if this is an update or create
      const isUpdate = row.ID && existingFlocMap.has(row.ID);

      // Build flocData object - use null for empty strings to explicitly clear fields
      const flocData: any = {
        code: row.Code,
        name: row.Name,
        parentFlocId: row.ParentFlocId || null,
        type: flocType as any,
        criticality: criticality as any,
        // Convert empty strings to null, keep actual values, omit undefined
        description: row.Description !== undefined ? (row.Description || null) : null,
        functionalSystem: row.FunctionalSystem !== undefined ? (row.FunctionalSystem || null) : null,
        criticalityJustification: row.CriticalityJustification !== undefined ? (row.CriticalityJustification || null) : null,
        requiredAssetType: row.RequiredAssetType !== undefined ? (row.RequiredAssetType || null) : null,
        address: row.Address !== undefined ? (row.Address || null) : null,
      };

      // Extra safety: remove any undefined values before passing to Firestore
      const cleanedData = removeUndefinedValues(flocData);

      if (isUpdate) {
        // Update existing FLOC
        await updateFloc(orgId, row.ID, cleanedData, userId);
        updated++;
      } else {
        // Create new FLOC
        await createFloc(orgId, cleanedData, userId);
        created++;
      }
    } catch (error) {
      errors.push(
        `Row ${rowNum} (${row.Name || 'Unknown'}): ${error instanceof Error ? error.message : 'Failed to import'}`
      );
    }
  }

  return { created, updated, errors };
}

/**
 * Import assets from parsed data
 */
async function importAssets(
  orgId: string,
  assetsData: any[],
  userId: string
): Promise<{ created: number; updated: number; errors: string[] }> {
  let created = 0;
  let updated = 0;
  const errors: string[] = [];

  // Get existing assets and FLOCs
  const [existingAssets, existingFlocs] = await Promise.all([
    getAssets(orgId),
    getFlocs(orgId),
  ]);
  const existingAssetMap = new Map(existingAssets.map((a: any) => [a.id, a]));
  const existingFlocIds = new Set(existingFlocs.map((f: any) => f.id));

  for (let i = 0; i < assetsData.length; i++) {
    const row = assetsData[i];
    const rowNum = i + 2; // Excel row number (1-indexed + header row)

    try {
      // Validate required fields
      if (!row.Name || typeof row.Name !== 'string') {
        errors.push(`Row ${rowNum}: Name is required`);
        continue;
      }

      // Auto-generate asset code if not provided
      const assetCode = row['Asset Code'] || `ASSET-${Date.now()}-${i}`;
      // Auto-generate type if not provided
      const assetType = row.Type || 'equipment';

      // Validate FLOC reference (if provided)
      if (row['FLOC ID'] && !existingFlocIds.has(row['FLOC ID'])) {
        errors.push(`Row ${rowNum}: FLOC ID '${row['FLOC ID']}' does not exist`);
        continue;
      }

      // Check if this is an update or create
      const isUpdate = row.ID && existingAssetMap.has(row.ID);

      // Build asset data for new FLOC model
      const assetData: CreateAssetData = {
        name: row.Name,
        assetId: assetCode,
        serialNumber: row['Serial Number'] || assetCode, // Serial number is required
        type: assetType,
        category: row.Category || '',
        storageLocation: row['Storage Location'] || 'Bulk Import',
        initialConditionScore: row['Condition Score'] ? Number(row['Condition Score']) : 100,
        manufacturer: row.Manufacturer || '',
        model: row.Model || '',
        description: row.Description || '',
      };

      if (isUpdate) {
        // Update existing asset
        await updateAsset(orgId, row.ID, assetData, userId);
        updated++;
      } else {
        // Create new asset (will be in storage with lifecycle status)
        await createAsset(orgId, assetData, userId);
        created++;
      }
    } catch (error) {
      errors.push(
        `Row ${rowNum} (${row.Name || 'Unknown'}): ${error instanceof Error ? error.message : 'Failed to import'}`
      );
    }
  }

  return { created, updated, errors };
}

/**
 * Import schedules from parsed data
 */
async function importSchedules(
  orgId: string,
  schedulesData: any[]
): Promise<{ created: number; updated: number; errors: string[] }> {
  let created = 0;
  let updated = 0;
  const errors: string[] = [];

  const [existingSchedules, existingFlocs, existingAssets, existingTemplates] = await Promise.all([
    getSchedules(orgId),
    getFlocs(orgId),
    getAssets(orgId),
    getTemplates(orgId),
  ]);

  const existingScheduleMap = new Map(existingSchedules.map((s: any) => [s.id, s]));
  const existingFlocIds = new Set(existingFlocs.map((f: any) => f.id));
  const existingAssetIds = new Set(existingAssets.map((a: any) => a.id));
  const existingTemplateIds = new Set(existingTemplates.map((t: any) => t.id));

  for (let i = 0; i < schedulesData.length; i++) {
    const row = schedulesData[i];
    const rowNum = i + 2;

    try {
      if (!row.Name || typeof row.Name !== 'string') {
        errors.push(`Row ${rowNum}: Name is required`);
        continue;
      }
      if (!row['Asset ID'] || typeof row['Asset ID'] !== 'string') {
        errors.push(`Row ${rowNum}: Asset ID is required`);
        continue;
      }
      if (!row['FLOC ID'] || typeof row['FLOC ID'] !== 'string') {
        errors.push(`Row ${rowNum}: FLOC ID is required`);
        continue;
      }
      if (!row['Template ID'] || typeof row['Template ID'] !== 'string') {
        errors.push(`Row ${rowNum}: Template ID is required`);
        continue;
      }

      if (!existingFlocIds.has(row['FLOC ID'])) {
        errors.push(`Row ${rowNum}: FLOC ID '${row['FLOC ID']}' does not exist`);
        continue;
      }
      if (!existingAssetIds.has(row['Asset ID'])) {
        errors.push(`Row ${rowNum}: Asset ID '${row['Asset ID']}' does not exist`);
        continue;
      }
      if (!existingTemplateIds.has(row['Template ID'])) {
        errors.push(`Row ${rowNum}: Template ID '${row['Template ID']}' does not exist`);
        continue;
      }

      const frequency = row['Recurrence Frequency'] || 'monthly';
      const interval = row['Recurrence Interval'] || 1;
      const daysOfWeek = row['Days of Week']
        ? row['Days of Week'].toString().split(',').map((d: string) => d.trim().toLowerCase())
        : undefined;
      const dayOfMonth = row['Day of Month'] ? parseInt(row['Day of Month']) : undefined;

      const recurrence = {
        frequency,
        interval: parseInt(interval),
        ...(daysOfWeek && { daysOfWeek }),
        ...(dayOfMonth && { dayOfMonth }),
      };

      const priority = row.Priority || 'medium';
      const isUpdate = row.ID && existingScheduleMap.has(row.ID);

      const scheduleData: CreateScheduleData = {
        name: row.Name,
        description: row.Description || '',
        assetId: row['Asset ID'],
        siteId: row['FLOC ID'],
        templateId: row['Template ID'],
        startDate: new Date(),
        recurrence,
        priority,
        assignedTo: row['Assigned To'] || undefined,
        leadTime: row['Lead Time (days)'] ? parseInt(row['Lead Time (days)']) : 0,
        approvalRequired: row['Approval Required'] === true || row['Approval Required'] === 'TRUE',
      };

      if (isUpdate) {
        await updateSchedule(orgId, row.ID, scheduleData);
        updated++;
      } else {
        const userId = row['Created By'] || 'system';
        await createSchedule(orgId, userId, scheduleData);
        created++;
      }
    } catch (error) {
      errors.push(
        `Row ${rowNum} (${row.Name || 'Unknown'}): ${error instanceof Error ? error.message : 'Failed to import'}`
      );
    }
  }

  return { created, updated, errors };
}
